import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ModuleAudio from "../components/ModuleAudio";
import { BookOpen, Clock, ChevronRight, ArrowLeft, Loader2 } from "lucide-react";
import SEO from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SatMaster — curriculum reader with two views:
 *   /sat-master              → module list (TOC)
 *   /sat-master/:slug        → single module reader
 */
export default function SatMaster() {
  const { slug } = useParams();
  return slug ? <ModuleReader slug={slug} /> : <ModuleList />;
}

// ── TOC view ────────────────────────────────────────────────────────────
function ModuleList() {
  const [mods, setMods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/curriculum/sat-master`)
      .then((r) => setMods(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMods([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen text-white" style={{ background: "#0E1A30" }}>
      <SEO
        title="SAT Master · Die Spielregeln des Lebens"
        description="Der SAT Master ist der Weg zu dir selbst — 24 geführte Wochenreisen in sechs Transformationsphasen. Persönliche Transformation, Lebensmeisterschaft und ein bewusstes Leben. Von Nenad Vladović."
        path="/sat-master"
      />
      <div className="container-sat py-20 md:py-28">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20 items-start">
          {/* Left: heading & poetic intro */}
          <div className="lg:sticky lg:top-32">
            <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-4 inline-flex items-center gap-2">
              <BookOpen size={12} /> SAT Master Curriculum
            </div>
            <h1 className="serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light leading-[1.04]">
              Der Transformations-Weg.
            </h1>
            <p className="serif italic text-white/55 mt-6 text-lg lg:text-xl max-w-md leading-relaxed">
              Ein begleiteter 6-Monats-Lehrgang. 24 Wochen. Schrittweise Entwicklung.
            </p>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5 max-w-md space-y-3 text-white/70 text-sm leading-relaxed">
              <p>
                SAT MASTER ist kein Selbstlernkurs zum Durchklicken. Es ist eine begleitete Reise,
                die dich Woche für Woche tiefer in deinen eigenen Human Transformation Code führt.
              </p>
              <p className="text-white/55">
                Aufnahme, Ablauf und Investition klären wir gemeinsam im persönlichen Erstgespräch.
              </p>
            </div>
            <div className="mt-8 rounded-2xl border border-[#D4AF37]/20 bg-white/[0.02] p-5 max-w-md" data-testid="sat-master-htc-note">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]/80 mb-2">
                Der rote Faden
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Im Hintergrund wirkt der <Link to="/sat-htc" className="text-[#D4AF37] hover:underline">SAT-HTC™ – Human Transformation Code</Link>.
                Er gibt dem SAT MASTER Prozess Struktur, Tiefe und Orientierung — vom Erkennen
                deiner Muster bis zur Verkörperung deines Ursprungscodes.
              </p>
            </div>
            <Link to="/erstgespraech"
                  className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-[#D4AF37] text-[#081328] hover:bg-[#D4AF37]/90 text-sm tracking-wide transition font-medium"
                  data-testid="sat-master-erstgespraech-cta">
              <BookOpen size={14} />
              Kostenloses Erstgespräch buchen
            </Link>
            <div className="mt-8 text-xs tracking-[0.3em] uppercase text-white/35">
              Nächste Aufnahmerunde · nach Erstgespräch
            </div>
          </div>

          {/* Right: 24-Wochen Programm-Übersicht (keine Direkt-Links auf geschützte Inhalte) */}
          <div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8 mb-8">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]/80 mb-4">
                Der Ablauf · 6 Monate · 24 Wochen
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm text-white/70">
                {[
                  { p: "Monat 1", t: "Grundlagen & Wesenscode", d: "Selbsterkenntnis · Schöpferbewusstsein" },
                  { p: "Monat 2", t: "Herkunft & Prägung",       d: "Vater · Mutter · Familiensystem" },
                  { p: "Monat 3", t: "Schutz & Schatten",         d: "Muster erkennen · Wahrheit fühlen" },
                  { p: "Monat 4", t: "Körper & Herz",             d: "Somatische Integration · Beziehung" },
                  { p: "Monat 5", t: "Glauben & Berufung",         d: "Recode · Werte · Sichtbarkeit" },
                  { p: "Monat 6", t: "Verkörperung & Serve",      d: "Alltag · Beitrag · Herzbewegung" },
                ].map((m, idx) => (
                  <div key={idx} className="border-l-2 border-[#D4AF37]/30 pl-4">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{m.p}</div>
                    <div className="serif text-white text-base mt-1">{m.t}</div>
                    <div className="text-white/50 text-xs mt-0.5">{m.d}</div>
                  </div>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="animate-spin text-[#D4AF37]" />
              </div>
            ) : (
              <div className="rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/[0.06] to-transparent p-8">
                <div className="serif text-2xl md:text-3xl font-light mb-4 text-white">
                  Der SAT MASTER Weg ist begleitet.
                </div>
                <p className="text-white/70 text-[15px] leading-relaxed mb-6">
                  Inhalte, Freischaltung, Community und persönliche Begleitung starten
                  nach dem gemeinsamen Erstgespräch. So wachsen wir gemeinsam — nicht im
                  Do-it-yourself.
                </p>
                <div className="text-white/45 text-xs italic mb-6">
                  Investition und Aufnahme werden im Erstgespräch transparent geklärt.
                </div>
                <Link
                  to="/erstgespraech"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-[#081328] hover:bg-[#D4AF37]/90 text-sm tracking-wide transition font-medium"
                  data-testid="sat-master-bottom-cta"
                >
                  <ChevronRight size={14} />
                  Kostenloses Erstgespräch buchen
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── (Alter, für Teilnehmer sichtbarer Modul-Reader bleibt bestehen unter /sat-master/:slug) ──

// ── Reader view ─────────────────────────────────────────────────────────
function ModuleReader({ slug }) {
  const [mod, setMod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setLoading(true);
    setErr(false);
    axios
      .get(`${API}/api/curriculum/sat-master/${slug}`)
      .then((r) => setMod(r.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0E1A30" }}>
        <Loader2 className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  if (err || !mod) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ background: "#0E1A30" }}>
        <div className="text-center">
          <p className="serif text-2xl">Dieses Modul ist noch nicht verfügbar.</p>
          <Link to="/sat-master" className="text-[#D4AF37] text-sm mt-4 inline-block">← Zurück zur Übersicht</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ background: "#0E1A30" }}>
      <div className="max-w-[760px] mx-auto px-6 lg:px-0 py-12 md:py-20">
        <Link
          to="/sat-master"
          className="inline-flex items-center gap-2 text-[#D4AF37] text-xs tracking-[0.25em] uppercase hover:translate-x-[-2px] transition mb-10"
          data-testid="sat-master-back"
        >
          <ArrowLeft size={12} /> Zurück zur Übersicht
        </Link>

        <article className="sat-master-prose" data-testid="sat-master-article">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{mod.body}</ReactMarkdown>
        </article>
      </div>

      {/* Sticky audio companion */}
      <ModuleAudio body={mod.body} title={mod.title} />
    </div>
  );
}
